import { useState, useEffect, useRef } from 'react';
import { getProducts, getCompanySettings } from '../services/api';

const PartsPage = () => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [partRequest, setPartRequest] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSku, setFilterSku] = useState('');
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionTag, setSectionTag] = useState('Spare Parts Catalog');
  const [sectionHeading, setSectionHeading] = useState('Parts Products');
  const [sectionDesc, setSectionDesc] = useState('Search our comprehensive spare parts inventory. Genuine and compatible parts with fast delivery nationwide.');
  const [waNumber, setWaNumber] = useState('6281234567890');
  const [companyEmail, setCompanyEmail] = useState('info@afsreman.com');
  const sectionRef = useRef(null);
  const filterPanelRef = useRef(null);
  const cartRef = useRef(null);

  // Fetch CMS settings
  useEffect(() => {
    getCompanySettings().then(data => {
      if (data) {
        if (data.parts_tag) setSectionTag(data.parts_tag);
        if (data.parts_heading) setSectionHeading(data.parts_heading);
        if (data.parts_description) setSectionDesc(data.parts_description);
        if (data.company_whatsapp) setWaNumber(String(data.company_whatsapp).replace(/[^0-9]/g, ''));
        if (data.company_email) setCompanyEmail(data.company_email);
      }
    });
  }, []);

  // Fetch products with category 'parts'
  useEffect(() => {
    getProducts({ category: 'parts', per_page: 50 }).then(data => {
      if (data && data.length > 0) {
        const mapped = data.map(p => ({
          id: p.id,
          no: p.sku || p.part_number || `P-${p.id}`,
          name: p.name || '',
          brand: p.brand?.name || p.brand || '-',
          type: p.type_unit?.name || p.category?.name || '-',
          sku: p.sku || p.part_number || '-',
        }));
        setParts(mapped);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const brands = [...new Set(parts.map(p => p.brand).filter(b => b && b !== '-'))].sort();

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );
    const els = sectionRef.current.querySelectorAll('.rv');
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  }, []);

  // Close cart dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (cartRef.current && !cartRef.current.contains(e.target)) setCartOpen(false);
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target) && !e.target.closest('.ap-filter-btn')) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = parts.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || [p.name, p.brand, p.type, p.sku, p.no].join(' ').toLowerCase().includes(q);
    const matchName = !filterName || p.name.toLowerCase().includes(filterName.toLowerCase());
    const matchBrand = !filterBrand || p.brand === filterBrand;
    const matchType = !filterType || p.type.toLowerCase().includes(filterType.toLowerCase());
    const matchSku = !filterSku || p.sku.toLowerCase().includes(filterSku.toLowerCase());
    return matchSearch && matchName && matchBrand && matchType && matchSku;
  });

  const allSelected = filtered.length > 0 && filtered.every(p => selected.includes(p.no));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(prev => prev.filter(no => !filtered.find(p => p.no === no)));
    } else {
      const filteredNos = filtered.map(p => p.no);
      setSelected(prev => [...new Set([...prev, ...filteredNos])]);
    }
  };

  const toggleSelect = (no) => {
    setSelected(prev => prev.includes(no) ? prev.filter(n => n !== no) : [...prev, no]);
  };

  const cartCount = selected.length;
  const cartItems = parts.filter(p => selected.includes(p.no));

  const handleContactSales = () => {
    let msg;
    if (cartCount === 0) {
      msg = `Hi AFS, I'd like to inquire about your spare parts catalog. Please share availability and pricing details. Thank you.`;
    } else {
      const lines = cartItems.map((p, i) => {
        const rows = [
          `${i + 1}. ${p.name}`,
          `   • No: ${p.no}`,
          p.brand && p.brand !== '-' ? `   • Brand: ${p.brand}` : null,
          p.type && p.type !== '-' ? `   • Type: ${p.type}` : null,
          p.sku && p.sku !== '-' ? `   • SKU: ${p.sku}` : null,
        ].filter(Boolean);
        return rows.join('\n');
      });
      msg = `Hi AFS, I'd like to inquire about the following parts (${cartCount} item${cartCount > 1 ? 's' : ''}):\n\n${lines.join('\n\n')}\n\nPlease confirm availability and pricing. Thank you.`;
    }
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterBrand('');
    setFilterType('');
    setFilterSku('');
  };

  const activeFilterCount = [filterName, filterBrand, filterType, filterSku].filter(Boolean).length;

  return (
    <section id="parts" ref={sectionRef} className="ap-section">
      <div className="wrap">
        <div className="ap-header rv">
          <div className="slide-index" style={{color:'var(--yd)'}}><span></span> {sectionTag}</div>
          <h2 className="heading">{(() => {
            const words = sectionHeading.split(' ');
            if (words.length >= 2) return <>{words.slice(0, -1).join(' ')} <em>{words[words.length - 1]}</em></>;
            return <em>{sectionHeading}</em>;
          })()}</h2>
          <p style={{color:'var(--mid)',fontSize:'15px',lineHeight:'1.75',marginTop:'14px',maxWidth:'600px'}}>{sectionDesc}</p>
        </div>
        <div className="ap-container rv">
          {/* Top Bar */}
          <div className="ap-topbar">
            <h2 className="ap-title">Available Parts</h2>
            <div className="ap-actions">
              <div className="ap-search-wrap">
                <svg className="ap-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                </svg>
                <input
                  type="text"
                  className="ap-search-input"
                  placeholder="Search part name or brand..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              {/* Filter Button */}
              <button className={`ap-filter-btn${activeFilterCount > 0 ? ' ap-filter-active' : ''}`} onClick={() => { setFilterOpen(!filterOpen); setCartOpen(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" x2="4" y1="21" y2="14"/><line x1="4" x2="4" y1="10" y2="3"/>
                  <line x1="12" x2="12" y1="21" y2="12"/><line x1="12" x2="12" y1="8" y2="3"/>
                  <line x1="20" x2="20" y1="21" y2="16"/><line x1="20" x2="20" y1="12" y2="3"/>
                  <line x1="2" x2="6" y1="14" y2="14"/><line x1="10" x2="14" y1="8" y2="8"/>
                  <line x1="18" x2="22" y1="16" y2="16"/>
                </svg>
                Filter{activeFilterCount > 0 && ` · ${activeFilterCount}`}
              </button>

              {/* Cart Button + Dropdown */}
              <div className="ap-dropdown-wrap" ref={cartRef}>
                <button className="ap-cart-btn" onClick={() => { setCartOpen(!cartOpen); setFilterOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
                    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57L23 6H6"/>
                  </svg>
                  Cart
                  {cartCount > 0 && <span className="ap-cart-badge">{cartCount}</span>}
                </button>
                {cartOpen && (
                  <div className="ap-dropdown ap-cart-dropdown">
                    <div className="ap-dropdown-header">
                      <span className="ap-dropdown-title">Selected Parts ({cartCount})</span>
                      {cartCount > 0 && <button className="ap-clear-btn" onClick={() => setSelected([])}>Clear all</button>}
                    </div>
                    {cartCount === 0 ? (
                      <p className="ap-cart-empty">No parts selected. Check the boxes in the table to add parts.</p>
                    ) : (
                      <>
                        <div className="ap-cart-list">
                          {cartItems.map(p => (
                            <div className="ap-cart-item" key={p.no}>
                              <div className="ap-cart-item-info">
                                <span className="ap-cart-item-name">{p.name}</span>
                                <span className="ap-cart-item-meta">{p.brand} · {p.sku}</span>
                              </div>
                              <button className="ap-cart-item-remove" onClick={() => toggleSelect(p.no)} aria-label="Remove">&times;</button>
                            </div>
                          ))}
                        </div>
                        <button className="ap-cart-contact-btn" onClick={handleContactSales}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
                            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57L23 6H6"/>
                          </svg>
                          Contact Sales ({cartCount})
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Panel — full width dropdown */}
          {filterOpen && (
            <div className="ap-filter-panel" ref={filterPanelRef}>
              <div className="ap-filter-grid">
                {/* Product Name */}
                <div className="ap-filter-col">
                  <label className="ap-fpanel-label">PRODUCT NAME</label>
                  <div className="ap-fpanel-input-wrap">
                    <span className="ap-fpanel-icon ap-fpanel-icon-search">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="ap-fpanel-input"
                      placeholder="Type product name..."
                      value={filterName}
                      onChange={e => setFilterName(e.target.value)}
                    />
                  </div>
                </div>
                {/* Brand */}
                <div className="ap-filter-col">
                  <label className="ap-fpanel-label">BRAND</label>
                  <div className="ap-fpanel-input-wrap">
                    <span className="ap-fpanel-icon ap-fpanel-icon-badge">B</span>
                    <select
                      className="ap-fpanel-select"
                      value={filterBrand}
                      onChange={e => setFilterBrand(e.target.value)}
                    >
                      <option value="">All brands</option>
                      {brands.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                {/* Type Unit */}
                <div className="ap-filter-col">
                  <label className="ap-fpanel-label">TYPE UNIT</label>
                  <div className="ap-fpanel-input-wrap">
                    <span className="ap-fpanel-icon ap-fpanel-icon-badge">T</span>
                    <input
                      type="text"
                      className="ap-fpanel-input"
                      placeholder="e.g. Excavator, Loader..."
                      value={filterType}
                      onChange={e => setFilterType(e.target.value)}
                    />
                  </div>
                </div>
                {/* Number Unit / SKU */}
                <div className="ap-filter-col">
                  <label className="ap-fpanel-label">NUMBER UNIT / SKU</label>
                  <div className="ap-fpanel-input-wrap">
                    <span className="ap-fpanel-icon ap-fpanel-icon-badge">#</span>
                    <input
                      type="text"
                      className="ap-fpanel-input"
                      placeholder="Type SKU / number..."
                      value={filterSku}
                      onChange={e => setFilterSku(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="ap-filter-footer">
                <span className="ap-filter-hint">Use filters to narrow down the parts list.</span>
                <button className="ap-filter-clear-btn" onClick={clearFilters}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" x2="4" y1="21" y2="14"/><line x1="4" x2="4" y1="10" y2="3"/>
                    <line x1="12" x2="12" y1="21" y2="12"/><line x1="12" x2="12" y1="8" y2="3"/>
                    <line x1="20" x2="20" y1="21" y2="16"/><line x1="20" x2="20" y1="12" y2="3"/>
                    <line x1="2" x2="6" y1="14" y2="14"/><line x1="10" x2="14" y1="8" y2="8"/>
                    <line x1="18" x2="22" y1="16" y2="16"/>
                  </svg>
                  Clear all filters
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th className="ap-th-check">
                    <span
                      className={`ap-checkbox${allSelected ? ' ap-checked' : ''}`}
                      onClick={toggleSelectAll}
                      role="checkbox"
                      aria-checked={allSelected}
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleSelectAll(); }}
                    >
                      {allSelected && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </span>
                  </th>
                  <th className="ap-th">PRODUCT NAME</th>
                  <th className="ap-th">BRAND</th>
                  <th className="ap-th">TYPE UNIT</th>
                  <th className="ap-th">SKU / NUMBER UNIT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="ap-empty">Loading parts...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="ap-empty">No matching parts found.</td>
                  </tr>
                ) : (
                  filtered.map((p) => (
                    <tr key={p.no} className={`ap-row${selected.includes(p.no) ? ' ap-row-selected' : ''}`}>
                      <td className="ap-td-check">
                        <span
                          className={`ap-checkbox${selected.includes(p.no) ? ' ap-checked' : ''}`}
                          onClick={() => toggleSelect(p.no)}
                          role="checkbox"
                          aria-checked={selected.includes(p.no)}
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleSelect(p.no); }}
                        >
                          {selected.includes(p.no) && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </span>
                      </td>
                      <td className="ap-td ap-td-name">{p.name}</td>
                      <td className="ap-td">{p.brand}</td>
                      <td className="ap-td">{p.type}</td>
                      <td className="ap-td ap-td-sku">{p.sku}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Bottom Bar */}
          <div className="ap-bottombar">
            <div className="ap-bottombar-right">
              <button className="ap-notavail-btn" onClick={() => setModalOpen(true)}>Part Not Available?</button>
              <button className="ap-contact-btn" onClick={handleContactSales}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
                  <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57L23 6H6"/>
                </svg>
                Contact Sales ({cartCount})
              </button>
            </div>
          </div>
        </div>

        {/* Modal — Request Unavailable Part */}
        {modalOpen && (
          <div className="ap-modal-overlay" onClick={() => setModalOpen(false)}>
            <div className="ap-modal" onClick={e => e.stopPropagation()}>
              <button className="ap-modal-close" onClick={() => setModalOpen(false)} aria-label="Close modal">&times;</button>
              <h3 className="ap-modal-title">Request Unavailable Part</h3>
              <p className="ap-modal-subtitle">Can't find the part you're looking for? Tell us what you need and we'll help you find it.</p>
              <label className="ap-modal-label">Part Name / SKU / Description</label>
              <textarea
                className="ap-modal-textarea"
                placeholder="e.g. Hydraulic Pump A10VSO 71 or describe the part you need..."
                value={partRequest}
                onChange={e => setPartRequest(e.target.value)}
              />
              <div className="ap-modal-divider" />
              <p className="ap-modal-contact-label">Choose how you want to contact our team:</p>
              <div className="ap-modal-buttons">
                <button
                  className="ap-modal-btn-email"
                  onClick={() => {
                    const subject = encodeURIComponent('Part Request');
                    const body = encodeURIComponent(partRequest || 'I need help finding a part.');
                    window.open(`mailto:${companyEmail}?subject=${subject}&body=${body}`, '_self');
                  }}
                >
                  Contact via Email
                </button>
                <button
                  className="ap-modal-btn-wa"
                  onClick={() => {
                    const msg = encodeURIComponent(partRequest || 'Hi, I need help finding a part.');
                    window.open(`https://wa.me/${waNumber}?text=${msg}`, '_blank');
                  }}
                >
                  Contact via WhatsApp
                </button>
              </div>
              <p className="ap-modal-fine">WhatsApp will open with your part request details. Email is optional for WhatsApp.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PartsPage;
